import React, { useCallback, useLayoutEffect } from 'react';
import { closedConnection, getBankConnections, ignoredCloseConnectionPrompt } from 'actions';
import { AppView, Button, ButtonGroup } from 'design-system';
import LogoBankAccount from 'design-system/LogoBankAccount';
import SuccessView from 'design-system/SuccessView';
import { muteErrorAndResult, useDisableAndroidBackButton, useFetchOld } from 'hooks';
import useColors from 'hooks/useColors';
import { useOpenConnectionLogin } from 'hooks/useOpenConnectionLogin';
import { useAppDispatch } from 'store/hooks';
import HelpScreenButton from 'features/help/components/HelpScreenButton';
import { HELP_ARTICLES_KEY } from 'features/help/constants';
import { isWeb } from '../../../constants';
import { canRenewConnection } from './utils';
const ConnectionAccountsClosedScreen = ({ navigation, route }) => {
    const { connection } = route.params;
    const colors = useColors();
    const dispatch = useAppDispatch();
    useLayoutEffect(() => {
        if (connection.isClosed) {
            navigation.pop();
            return;
        }
        navigation.setOptions({
            title: 'Close bank connection',
            headerTitle: '',
            headerTintColor: colors.text.primary,
            gestureEnabled: isWeb,
            headerBackVisible: isWeb,
            headerRight: () => <HelpScreenButton articlesKey={HELP_ARTICLES_KEY.BANK_CONNECT}/>,
        });
    }, [colors.text.primary, connection.isClosed]);
    useDisableAndroidBackButton();
    const keepOpen = useCallback(() => {
        dispatch(ignoredCloseConnectionPrompt(connection.id, true));
        navigation.pop();
    }, [connection.id]);
    const login = useOpenConnectionLogin(navigation);
    const updateConnection = useCallback(() => {
        login({ type: 'connection', data: connection }, false);
    }, [connection, login]);
    const [, , isClosing, fetch] = useFetchOld(muteErrorAndResult);
    const onClose = useCallback(async () => {
        const res = await fetch(closedConnection(connection));
        if (res) {
            dispatch(getBankConnections());
            navigation.pop();
        }
    }, [connection]);
    const subtitle = `We can no longer find any active accounts with ${connection.bankInfo.name}. If you’ve recently opened a new one, please update your connection.\n\nOtherwise, you can mark this connection as closed. We’ll stop syncing, but your past data will stay in Emma.`;
    const canRenew = canRenewConnection(connection);
    return (<AppView withFrame={false}>
      <SuccessView disableFadeAnimation icon={<LogoBankAccount size="R56" data={connection.bankInfo}/>} title={`Your ${connection.bankInfo.name} accounts are closed`} subTitle={subtitle}>
        <ButtonGroup type="floating">
          <Button {...(canRenew ? { brandBorder: true } : { brandReversed: true })} title="Keep connection open" onPress={keepOpen} disabled={isClosing}/>
          {!!canRenew && (<Button brandReversed title="Update connection" onPress={updateConnection} disabled={isClosing}/>)}
          <Button brand title="Mark as closed" onPress={onClose} isFetching={isClosing}/>
        </ButtonGroup>
      </SuccessView>
    </AppView>);
};
export default ConnectionAccountsClosedScreen;
